import { useMemo, useState } from 'react';
import { CalendarDays, CalendarOff, Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { use_services } from '@/features/vendor-service/hooks';
import {
  use_availability,
  use_day_view,
  use_create_rule,
  use_delete_rule,
  use_create_exception,
  use_delete_exception,
  use_update_config,
} from '@/features/availability/hooks';
import type { AvailabilityException, AvailabilityRule, VendorModuleType } from '@/features/availability/types';

const IST = 'Asia/Kolkata';
const today_ist = () => new Date().toLocaleDateString('en-CA', { timeZone: IST });
const slot_label = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit' });

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// No capacity field: slots are added one booking at a time (client request, 9 Oct 2026);
// the backend defaults capacity to 1.
type WindowDraft = { start_time: string; end_time: string };
const EMPTY_WINDOW: WindowDraft = { start_time: '09:00', end_time: '17:00' };

export function VendorAvailabilityPage() {
  const draft_q = use_draft();
  const availability_q = use_availability();
  const create_rule_m = use_create_rule();
  const delete_rule_m = use_delete_rule();
  const create_exception_m = use_create_exception();
  const delete_exception_m = use_delete_exception();
  const update_config_m = use_update_config();

  const modules = (draft_q.data?.module_types ?? []) as VendorModuleType[];
  const [active_module, set_active_module] = useState<VendorModuleType | ''>('');
  const module_type = active_module || modules[0] || '';

  const rules = availability_q.data?.rules ?? [];
  const exceptions = availability_q.data?.exceptions ?? [];
  const config = availability_q.data?.config;

  // Recurring windows for the selected module, grouped by weekday (date rules excluded).
  const by_weekday = useMemo(() => {
    const map = new Map<number, AvailabilityRule[]>();
    for (let d = 0; d < 7; d++) map.set(d, []);
    for (const r of rules) {
      if (r.module_type === module_type && r.weekday !== null) map.get(r.weekday)!.push(r);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [rules, module_type]);

  // Whole-day blackouts only (per-slot blocks are managed in the date view).
  const module_exceptions = exceptions
    .filter((e) => e.module_type === module_type && e.slot_start === null)
    .sort((a, b) => a.date.localeCompare(b.date));

  const [open_weekday, set_open_weekday] = useState<number | null>(null);
  const [window_draft, set_window_draft] = useState<WindowDraft>(EMPTY_WINDOW);
  const [rule_error, set_rule_error] = useState<string | null>(null);

  const [blackout_date, set_blackout_date] = useState('');
  const [blackout_reason, set_blackout_reason] = useState('');
  const [blackout_error, set_blackout_error] = useState<string | null>(null);

  const [cfg_notice, set_cfg_notice] = useState<string>('');
  const [cfg_horizon, set_cfg_horizon] = useState<string>('');
  const [cfg_error, set_cfg_error] = useState<string | null>(null);
  const [cfg_saved, set_cfg_saved] = useState(false);

  function open_add_window(weekday: number) {
    set_rule_error(null);
    set_window_draft(EMPTY_WINDOW);
    set_open_weekday(weekday);
  }

  async function add_window(weekday: number) {
    set_rule_error(null);
    if (!module_type) return;
    if (window_draft.start_time >= window_draft.end_time) {
      return set_rule_error('Opening time must be before closing time.');
    }
    try {
      await create_rule_m.mutateAsync({
        module_type,
        weekday,
        start_time: window_draft.start_time,
        end_time: window_draft.end_time,
      });
      set_open_weekday(null);
    } catch (err) {
      set_rule_error(form_error_message(err, 'Could not add this window.'));
    }
  }

  async function remove_window(rule: AvailabilityRule) {
    await delete_rule_m.mutateAsync(rule.id);
  }

  async function add_blackout() {
    set_blackout_error(null);
    if (!module_type) return;
    if (!blackout_date) return set_blackout_error('Pick a date to block.');
    try {
      await create_exception_m.mutateAsync({
        module_type,
        date: blackout_date,
        reason: blackout_reason.trim() || undefined,
      });
      set_blackout_date('');
      set_blackout_reason('');
    } catch (err) {
      set_blackout_error(form_error_message(err, 'Could not add this blackout date.'));
    }
  }

  async function save_config() {
    set_cfg_error(null);
    set_cfg_saved(false);
    const notice = cfg_notice.trim() === '' ? config?.min_notice_hours : Number(cfg_notice);
    const horizon = cfg_horizon.trim() === '' ? config?.horizon_days : Number(cfg_horizon);
    // Bounds mirror the API (min_notice_hours 0–168, horizon_days 1–365) so an
    // out-of-range value is caught here instead of coming back as a 422.
    if (notice === undefined || !Number.isInteger(notice) || notice < 0 || notice > 168) {
      return set_cfg_error('Minimum notice must be a whole number of hours between 0 and 168 (7 days).');
    }
    if (horizon === undefined || !Number.isInteger(horizon) || horizon < 1 || horizon > 365) {
      return set_cfg_error('Booking horizon must be between 1 and 365 days.');
    }
    try {
      await update_config_m.mutateAsync({ min_notice_hours: notice, horizon_days: horizon });
      set_cfg_notice('');
      set_cfg_horizon('');
      set_cfg_saved(true);
    } catch (err) {
      set_cfg_error(form_error_message(err, 'Could not save scheduling settings.'));
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Availability"
        description="Set the weekly hours customers can book, block off specific dates, and tune booking rules."
      />

      {modules.length === 0 && !draft_q.isLoading && (
        <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning" data-testid="availability-no-modules">
          Finish onboarding (pick at least one module) before setting availability.
        </div>
      )}

      {/* Module tabs */}
      {modules.length > 1 && (
        <div className="flex flex-wrap gap-1" data-testid="availability-module-tabs">
          {modules.map((m) => (
            <Button
              key={m}
              size="sm"
              variant={m === module_type ? 'gradient' : 'ghost'}
              onClick={() => set_active_module(m)}
              data-testid={`availability-module-${m}`}
            >
              {m}
            </Button>
          ))}
        </div>
      )}

      {availability_q.isLoading ? (
        <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : availability_q.error ? (
        <div className="p-6 text-sm text-destructive" data-testid="availability-error">
          {form_error_message(availability_q.error, 'Failed to load availability.')}
        </div>
      ) : (
        module_type && (
          <>
            {/* Weekly windows */}
            <Card>
              <CardContent className="flex flex-col gap-1 p-0">
                <div className="flex items-center gap-2 border-b border-border p-4">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="font-heading font-semibold">Weekly hours · {module_type}</span>
                </div>
                <ul className="divide-y divide-border" data-testid="availability-weekdays">
                  {WEEKDAYS.map((label, weekday) => {
                    const windows = by_weekday.get(weekday)!;
                    return (
                      <li key={weekday} className="flex flex-col gap-2 p-4" data-testid={`weekday-${weekday}`}>
                        <div className="flex items-center justify-between">
                          <span className="w-28 font-semibold">{label}</span>
                          <div className="flex flex-1 flex-wrap items-center gap-2">
                            {windows.length === 0 ? (
                              <span className="text-sm text-muted-foreground">Closed</span>
                            ) : (
                              windows.map((w) => (
                                <span
                                  key={w.id}
                                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium"
                                  data-testid={`window-${w.id}`}
                                >
                                  {w.start_time}–{w.end_time}
                                  {w.capacity > 1 && <span className="text-muted-foreground">· {w.capacity} seats</span>}
                                  <button
                                    type="button"
                                    aria-label="Remove window"
                                    onClick={() => remove_window(w)}
                                    data-testid={`window-delete-${w.id}`}
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </span>
                              ))
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => open_add_window(weekday)}
                            data-testid={`add-window-${weekday}`}
                          >
                            <Plus className="h-4 w-4" /> Window
                          </Button>
                        </div>

                        {open_weekday === weekday && (
                          <div className="flex flex-wrap items-end gap-3 rounded-md bg-muted/40 p-3" data-testid={`window-form-${weekday}`}>
                            <div className="flex flex-col gap-1">
                              <Label htmlFor={`start-${weekday}`}>Opens</Label>
                              <TimePicker
                                id={`start-${weekday}`}
                                value={window_draft.start_time}
                                onChange={(v) => set_window_draft((d) => ({ ...d, start_time: v }))}
                                className="w-36"
                                data-testid={`window-start-${weekday}`}
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <Label htmlFor={`end-${weekday}`}>Closes</Label>
                              <TimePicker
                                id={`end-${weekday}`}
                                value={window_draft.end_time}
                                onChange={(v) => set_window_draft((d) => ({ ...d, end_time: v }))}
                                className="w-36"
                                data-testid={`window-end-${weekday}`}
                              />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => add_window(weekday)}
                              disabled={create_rule_m.isPending}
                              data-testid={`window-save-${weekday}`}
                            >
                              {create_rule_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              Add
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => set_open_weekday(null)}>
                              Cancel
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {rule_error && (
                  <p className="px-4 pb-3 text-sm text-destructive" data-testid="window-error">
                    {rule_error}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Blackout dates */}
            <Card>
              <CardContent className="flex flex-col gap-3 p-4">
                <div className="flex items-center gap-2">
                  <CalendarOff className="h-4 w-4 text-primary" />
                  <span className="font-heading font-semibold">Blackout dates</span>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="blackout-date">Date</Label>
                    <DatePicker
                      id="blackout-date"
                      value={blackout_date}
                      min={today_ist()}
                      onChange={set_blackout_date}
                      className="w-44"
                      data-testid="blackout-date"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-1">
                    <Label htmlFor="blackout-reason">Reason (optional)</Label>
                    <Input
                      id="blackout-reason"
                      value={blackout_reason}
                      onChange={(e) => set_blackout_reason(e.target.value)}
                      placeholder="Public holiday"
                      data-testid="blackout-reason"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={add_blackout}
                    disabled={create_exception_m.isPending}
                    data-testid="blackout-add"
                  >
                    {create_exception_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Block date
                  </Button>
                </div>
                {blackout_error && (
                  <p className="text-sm text-destructive" data-testid="blackout-error">
                    {blackout_error}
                  </p>
                )}
                {module_exceptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="blackout-empty">
                    No blocked dates.
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2" data-testid="blackout-list">
                    {module_exceptions.map((e) => (
                      <li
                        key={e.id}
                        className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-medium"
                        data-testid={`blackout-${e.id}`}
                      >
                        {e.date}
                        {e.reason && <span className="text-muted-foreground">· {e.reason}</span>}
                        <button
                          type="button"
                          aria-label="Remove blackout"
                          onClick={() => delete_exception_m.mutate(e.id)}
                          data-testid={`blackout-delete-${e.id}`}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Scheduling config */}
            <Card>
              <CardContent className="flex flex-col gap-3 p-4">
                <span className="font-heading font-semibold">Booking rules</span>
                <p className="text-sm text-muted-foreground" data-testid="booking-rules-hint">
                  <span className="font-medium text-foreground">Booking horizon</span> caps how far ahead customers can
                  book: they only see open times within this many days from now. Your weekly hours and one-off windows
                  further out stay hidden from customers until they fall inside this window.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="cfg-notice">Minimum notice (hours)</Label>
                    <Input
                      id="cfg-notice"
                      type="number"
                      min={0}
                      value={cfg_notice}
                      onChange={(e) => {
                        set_cfg_notice(e.target.value);
                        set_cfg_saved(false);
                      }}
                      placeholder={config ? String(config.min_notice_hours) : '2'}
                      className="w-44"
                      data-testid="cfg-notice"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="cfg-horizon">Booking horizon (days)</Label>
                    <Input
                      id="cfg-horizon"
                      type="number"
                      min={1}
                      value={cfg_horizon}
                      onChange={(e) => {
                        set_cfg_horizon(e.target.value);
                        set_cfg_saved(false);
                      }}
                      placeholder={config ? String(config.horizon_days) : '30'}
                      className="w-44"
                      data-testid="cfg-horizon"
                    />
                  </div>
                  <Button size="sm" onClick={save_config} disabled={update_config_m.isPending} data-testid="cfg-save">
                    {update_config_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save rules
                  </Button>
                </div>
                {cfg_error && (
                  <p className="text-sm text-destructive" data-testid="cfg-error">
                    {cfg_error}
                  </p>
                )}
                {cfg_saved && (
                  <p className="text-sm text-success" data-testid="cfg-saved">
                    Saved.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Manage a specific date */}
            <DateManager module_type={module_type} exceptions={exceptions} />
          </>
        )
      )}
    </div>
  );
}

/** Per-date view: pick a service + date → the day's slot grid with per-slot
 *  block/unblock toggles, plus an "add one-off window" form. */
function DateManager({ module_type, exceptions }: { module_type: VendorModuleType; exceptions: AvailabilityException[] }) {
  const services_q = use_services();
  const services = (services_q.data ?? []).filter((s) => s.module_type === module_type);

  const [service_id, set_service_id] = useState('');
  const sid = service_id || services[0]?.id || '';
  const [date, set_date] = useState(today_ist());

  const day_q = use_day_view(sid || undefined, date);
  const create_rule_m = use_create_rule();
  const create_exception_m = use_create_exception();
  const delete_exception_m = use_delete_exception();

  const [win, set_win] = useState({ start_time: '09:00', end_time: '17:00' });
  const [error, set_error] = useState<string | null>(null);

  // Map a blocked slot_start → its exception id, so we can unblock by deleting it.
  const block_id_by_slot = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of exceptions) if (e.slot_start) m.set(e.slot_start, e.id);
    return m;
  }, [exceptions]);

  async function add_window() {
    set_error(null);
    if (win.start_time >= win.end_time) return set_error('Opening time must be before closing time.');
    try {
      await create_rule_m.mutateAsync({ module_type, date, start_time: win.start_time, end_time: win.end_time });
    } catch (err) {
      set_error(form_error_message(err, 'Could not add this window.'));
    }
  }

  async function toggle(slot_start: string, blocked: boolean) {
    set_error(null);
    try {
      if (blocked) {
        const id = block_id_by_slot.get(slot_start);
        if (id) await delete_exception_m.mutateAsync(id);
      } else {
        await create_exception_m.mutateAsync({ module_type, date, slot_start });
      }
    } catch (err) {
      set_error(form_error_message(err, 'Could not update this slot.'));
    }
  }

  const slots = day_q.data?.slots ?? [];
  // The management grid deliberately shows slots past the customer booking horizon
  // so they can be pre-managed. Flag those so the vendor knows customers can't book
  // them yet (mirrors the user-side rule: bookable only within now + horizon days).
  const horizon_days = day_q.data?.horizon_days;
  const horizon_cutoff_ms =
    horizon_days != null ? Date.now() + horizon_days * 86_400_000 : null;
  const is_beyond_window = (slot_start: string) =>
    horizon_cutoff_ms != null && new Date(slot_start).getTime() > horizon_cutoff_ms;
  const any_beyond = slots.some((s) => is_beyond_window(s.slot_start));

  return (
    <Card data-testid="date-manager">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <span className="font-heading font-semibold">Manage a specific date</span>
        </div>

        {services.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="date-manager-no-service">
            Add {/^[AEIOU]/.test(module_type) ? 'an' : 'a'} {module_type} service first to manage
            its slots for a date.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="dm-service">Service</Label>
                <SelectField
                  id="dm-service"
                  value={sid}
                  onValueChange={set_service_id}
                  data-testid="dm-service"
                  className="w-56"
                  options={services.map((s) => ({ value: s.id, label: s.name }))}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="dm-date">Date</Label>
                <DatePicker id="dm-date" min={today_ist()} value={date} onChange={set_date} className="w-44" data-testid="dm-date" />
              </div>
            </div>

            {/* Add one-off window for this date */}
            <div className="flex flex-wrap items-end gap-3 rounded-md bg-muted/40 p-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="dm-start">Opens</Label>
                <TimePicker id="dm-start" value={win.start_time} onChange={(v) => set_win((w) => ({ ...w, start_time: v }))} className="w-36" data-testid="dm-start" />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="dm-end">Closes</Label>
                <TimePicker id="dm-end" value={win.end_time} onChange={(v) => set_win((w) => ({ ...w, end_time: v }))} className="w-36" data-testid="dm-end" />
              </div>
              <Button size="sm" onClick={add_window} disabled={create_rule_m.isPending} data-testid="dm-add-window">
                {create_rule_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add window for this date
              </Button>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="dm-error">
                {error}
              </p>
            )}

            {day_q.data?.is_blackout && (
              <p className="rounded-md bg-warning/10 p-2 text-sm text-warning" data-testid="dm-blackout">
                This date is fully blacked out for {module_type}.
              </p>
            )}

            {/* Slot grid with block toggles */}
            {day_q.isLoading ? (
              <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="dm-empty">
                No slots on this date. Add a one-off window above, or set weekly hours.
              </p>
            ) : (
              <>
                {any_beyond && (
                  <p
                    className="rounded-md bg-muted/60 p-2 text-xs text-muted-foreground"
                    data-testid="dm-beyond-window-note"
                  >
                    Times marked <span className="font-semibold">“beyond window”</span> are further ahead than your{' '}
                    {horizon_days}-day booking horizon — customers can’t book them yet. Raise the horizon in{' '}
                    <span className="font-medium text-foreground">Booking rules</span> to open them up.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="dm-grid">
                  {slots.map((s) => {
                    const beyond = is_beyond_window(s.slot_start);
                    return (
                      <button
                        key={s.slot_start}
                        type="button"
                        onClick={() => toggle(s.slot_start, s.blocked)}
                        data-testid={`dm-slot-${s.slot_start}`}
                        data-beyond-window={beyond ? '' : undefined}
                        className={`flex flex-col items-center rounded-button border-2 px-2 py-2.5 text-sm font-semibold transition-all duration-fast ease-bounce active:scale-95 ${
                          s.blocked
                            ? 'border-destructive/30 bg-destructive/10 text-destructive line-through'
                            : beyond
                              ? 'border-dashed border-input/70 bg-muted/40 text-muted-foreground'
                              : 'border-input hover:border-primary/50 hover:bg-muted'
                        }`}
                        title={
                          s.blocked
                            ? 'Blocked — click to open'
                            : beyond
                              ? `Beyond your ${horizon_days}-day booking horizon — customers can't book this yet. Click to block.`
                              : 'Open — click to block'
                        }
                      >
                        {slot_label(s.slot_start)}
                        {beyond && !s.blocked && (
                          <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">beyond window</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
